import {appInstance} from '~/utils/app-accessor'
import {CHANGE_LOCALE_EVENT, EventBus} from '~/utils/event-bus'
import type {Ref} from 'vue'
import {contractServiceType} from '~/utils/types/types'

export const useSupplierStore = defineStore('supplier', () => {
    const orders: Ref<any[]> = ref([])
    const dailyRates: Ref<any[]> = ref([])
    const availability: Ref<any[]> = ref([])
    const services: Ref<any[]> = ref([])
    const tariffs: Ref<any[]> = ref([])
    const tariffsForPrice: Ref<any[]> = ref([])
    const prices: Ref<any[]> = ref([])
    const contract: Ref = ref({})
    const fullContract: Ref = ref({})
    const isLoading: Ref<boolean> = ref(false)
    const isHotelInfoLoading: Ref<boolean> = ref(false)
    const hotelInfo: Ref = ref({})
    const hotelServiceDictionaryInfo: Ref = ref({})
    const paxes: Ref<any[]> = ref([])
    const hotelChainDictionary: Ref<any[]> = ref([])
    const isAmenitiesLoading: Ref<boolean> = ref(false)
    const catalogue: Ref<any[]> = ref([])
    const productServices: Ref<any[]> = ref([])
    const charterServiceCategories: Ref<any[]> = ref([])
    const offices: Ref<any[]> = ref([])
    const locationPointsDictionary = ref([])
    const categoryDictionary = ref([])
    const hotelTypesDictionary = ref([])
    const contentProviders = ref([])
    const mealTypes = ref([])
    const roomTypes = ref([])

    async function loadOrders() {
        let startDate = new Date()
        startDate.setMonth(startDate.getMonth() - 6)
        const endDate = new Date()
        try {
            const rs = await appInstance.$api.orders.get({
                orderType: 'SUPPLIER',
                responseType: 'SHORT',
                createdDateFrom: startDate,
                createdDateTo: endDate,
            })

            orders.value = rs.orders
        } catch (e) {
            orders.value = []
        }
    }

    async function loadContract(id: any) {
        try {
            const {
                contracts: [_contract],
            } = await appInstance.$api.contracts.get({id})

            if (_contract) {
                contract.value = _contract
            } else {
                const {
                    contracts: [_contract],
                } = await appInstance.$api.contracts.get({id, active: false})
                contract.value = _contract
            }
        } catch (e) {
            contract.value = {}
        }
    }

    const methods = {
        ownCharter: 'ownCharterContracts',
        ownExcursion: 'ownExcursionContracts',
        concert: 'ownEventContracts',
        ownTransfer: 'ownTransferContracts',
        ownCarRent: 'ownCarRentContracts',
        tour: 'ownTourContracts',
        ownCruise: 'ownCruiseContracts',
        ownVisa: 'ownVisaContracts',
        ownExtraService: 'ownExtraServiceContracts',
        ownInsurance: 'ownInsuranceContracts',
        ownHotel: 'ownAccommodationContracts',
        customComplexTour: 'ownPackageContracts',
    }

    function clearAllContractInfo() {
        contract.value = {}
        fullContract.value = {}
        hotelInfo.value = {}
        hotelServiceDictionaryInfo.value = {}
        hotelChainDictionary.value = []
        categoryDictionary.value = []
        hotelTypesDictionary.value = []
    }

    async function loadFullContract(): Promise<void> {
        // ZSH FIX: Guard against undefined or empty contract to prevent "Cannot read properties of undefined" error
        if (!contract.value?.serviceType || !contract.value?.id) {
            // Don't set fullContract to empty object - just return early
            // This prevents the error while allowing the contract to load properly later
            return
        }

        const appMethod = methods[contract.value.serviceType]
        try {
            isLoading.value = true
            const rq = {id: contract.value.id, locale: 'all'}

            const {
                ownProductContracts: [_contract],
            } = await appInstance.$api[appMethod].get(rq)
            fullContract.value = _contract

            if (contract.value.serviceType === contractServiceType.ownHotel) {
                await loadHotelInfo()
            }
        } catch (e) {
            fullContract.value = {}
        } finally {
            isLoading.value = false
        }
    }

    async function loadHotelInfo(id?): Promise<void> {
        const hotelId = id ? id : fullContract.value?.hotelContentId
        if (hotelId) {
            try {
                isHotelInfoLoading.value = true
                hotelInfo.value = await appInstance.$api.ownHotelInfo.get(hotelId, {
                    locale: 'all',
                })
                await loadHotelData()
            } catch (e) {
                hotelInfo.value = {}
            } finally {
                isHotelInfoLoading.value = false
            }
        }
    }

    async function loadHotelData(): Promise<void> {
        // amenities
        loadHotelServiceDictionaryInfo()
        loadHotelChainDictionaryInfo()
        loadHotelCategoryDictionaryInfo()
        loadHotelTypesDictionaryInfo()
    }

    async function loadHotelTypesDictionaryInfo(): Promise<void> {
        try {
            hotelTypesDictionary.value = await appInstance.$api.hotelTypesDictionaryInfo.get({
                supplierId: hotelInfo.value?.supplierId || 1,
            })
        } catch (e) {
            hotelTypesDictionary.value = []
        }
    }

    async function loadHotelCategoryDictionaryInfo(): Promise<void> {
        try {
            const locale = appInstance.$i18n.locale.value
            const dictionary = await appInstance.$api.hotelCategoryDictionaryInfo.get({
                supplierId: hotelInfo.value?.supplierId || 1,
                locale,
            })
            categoryDictionary.value = dictionary.sort((a, b) => a.name.localeCompare(b.name))
        } catch (e) {
            categoryDictionary.value = []
        }
    }

    async function loadContentProviders(product): Promise<void> {
        try {
            contentProviders.value = (await appInstance.$api.contentProviders.get({product}))
                .sort((a, b) => a.name.localeCompare(b.name))
                .filter(({id}) => id !== 1)
        } catch (e) {
            contentProviders.value = []
        }
    }

    async function loadHotelServiceDictionaryInfo(): Promise<void> {
        try {
            isAmenitiesLoading.value = true
            const {dictionary} = await appInstance.$api.hotelServiceDictionaryInfo.get({
                supplierId: hotelInfo.value?.supplierId || 1,
            })
            const desiredOrder = [
                'BOARD_TYPE',
                'BUSSINESS_SERVICES',
                'KIDS',
                'CARDS',
                'HEALTH_CARE_SERVICES',
                'DEFAULT',
                'IN_HOTEL',
                'IN_ROOM',
                'SECURITY',
                'SPORT',
            ]
            const keys = Object.keys(dictionary)
            const sortedKeys = keys.sort((a, b) => desiredOrder.indexOf(a) - desiredOrder.indexOf(b))
            const sortedObject = {}
            sortedKeys.forEach(key => {
                sortedObject[key] = dictionary[key]
            })

            hotelServiceDictionaryInfo.value = sortedObject
        } catch (e) {
            hotelServiceDictionaryInfo.value = {}
        } finally {
            isAmenitiesLoading.value = false
        }
    }

    async function loadHotelChainDictionaryInfo(): Promise<void> {
        try {
            hotelChainDictionary.value = await appInstance.$api.hotelChainDictionaryInfo.get({
                supplierId: hotelInfo.value?.supplierId || 1,
            })
        } catch (e) {
            hotelChainDictionary.value = []
        }
    }

    async function loadHotelLocationDictionaryInfo(): Promise<void> {
        try {
            locationPointsDictionary.value = (await appInstance.$api.hotelLocationDictionaryInfo.get()).sort((a, b) =>
                a.name.localeCompare(b.name)
            )
        } catch (e) {
            locationPointsDictionary.value = []
        }
    }

    async function loadPaxes(id?): Promise<void> {
        const contractId = id ? id : fullContract.value?.id
        try {
            const rq = {contractId, active: true}
            paxes.value = (await appInstance.$api.ownProductPaxes.get(rq)).paxes
        } catch (e) {
            paxes.value = []
        }
    }

    async function loadAvailability(rq: any) {
        try {
            const rs = await appInstance.$api.availability.get(Object.assign(rq, {allotmentType: 'NETTO'}))
            availability.value = rs.inventories
        } catch (e) {
            availability.value = []
        }
    }

    async function loadDailyRates(rq: any) {
        try {
            const rs = await appInstance.$api.dailyRates.get(rq)
            dailyRates.value = rs.rates.dailyRates
            services.value = rs.rates.services
            tariffs.value = rs.rates.tariffs
        } catch (e) {
            dailyRates.value = []
            services.value = []
            tariffs.value = []
        }
    }

    async function loadPrices(rq: any) {
        try {
            prices.value = await appInstance.$api.prices.get(rq)
        } catch (e) {
            prices.value = []
        }
    }

    async function loadTariffs(rq: any) {
        try {
            const [activeTariffs, inactiveTariffs] = await Promise.all([
                appInstance.$api.tariffs.get({...rq, active: true}),
                appInstance.$api.tariffs.get({...rq, active: false}),
            ])
            tariffsForPrice.value = [...activeTariffs.tariffs, ...inactiveTariffs.tariffs]
        } catch (e) {
            tariffsForPrice.value = []
        }
    }

    async function reload() {
        if (orders.value.length) await loadOrders()

        if (productServices.value.length) await loadProductServices()
        if (catalogue.value.length) await loadCatalogue()

        locationPointsDictionary.value = []
    }

    const internalSuppliers: Ref<any[]> = ref([])
    async function loadSuppliersForContract() {
        const rq = {relationship: 'SUPPLIER', type: 'INTERNAL_SUPPLIER', shortResponse: true, order: 'ALPHABET'}
        const rs = await appInstance.$api.companies.get(rq)
        internalSuppliers.value = [authStore.company, ...rs.companies]
    }

    function clientInit() {
        EventBus.$on(CHANGE_LOCALE_EVENT, reload)
        EventBus.$on(B2B_AUTH_EVENT, reset)
    }

    async function reset() {
        await resetSalesChannels()
    }

    const availabilityServices = computed(() => {
        if (!availability.value.length) return []
        return availability.value.filter(av => av.name === 'Allotment')[0].availabilityType
    })

    async function loadCatalogue(): Promise<void> {
        try {
            const rq = {contractId: fullContract.value?.id, active: true}
            const {categories} = await appInstance.$api.ownProductServiceCategories.get(rq)
            catalogue.value = categories.sort((a, b) => a.name.localeCompare(b.name))
        } catch (e) {
            catalogue.value = []
        }
    }

    async function loadProductServices(): Promise<void> {
        try {
            const rq = {contractId: fullContract.value?.id || contract.value?.id}
            const {services} = await appInstance.$api.ownProductServices.get(rq)
            productServices.value = services
        } catch (e) {
            productServices.value = []
        }
    }

    async function loadHotelSecondaryServices(): Promise<void> {
        try {
            const rq = {contractId: fullContract.value?.id || contract.value?.id}
            const {services} = await appInstance.$api.ownHotelSecondaryServices.get(rq)
            mealTypes.value = services
        } catch (e) {
            mealTypes.value = []
        }
    }

    const secondaryServices = ref([])
    async function loadOwnProductSecondaryServices(): Promise<void> {
        try {
            const rq = {contractId: contract.value.id}
            secondaryServices.value = (await appInstance.$api.ownProductSecondaryServices.get(rq)).services
        } catch (e) {
            secondaryServices.value = []
        }
    }

    async function loadRoomTypeData(): Promise<void> {
        try {
            const {hotelRoomTypes} = await appInstance.$api.hotelDescriptionRoomType.get(hotelInfo.value.hotelId)
            roomTypes.value = hotelRoomTypes.filter(({active}) => active)
        } catch (e) {
            roomTypes.value = []
        }
    }

    async function loadCharterServiceCategories(): Promise<void> {
        try {
            const rq = {contractId: fullContract.value?.id, active: true}
            charterServiceCategories.value = await appInstance.$api.ownCharterServiceCategories.get(rq)
        } catch (e) {
            charterServiceCategories.value = []
        }
    }

    async function loadOffices(): Promise<void> {
        try {
            const rq = {companyId: fullContract.value?.supplierId}
            const res = await appInstance.$api.offices.get(rq)
            offices.value = res.offices
        } catch (e) {
            offices.value = []
        }
    }

    const programs: Ref = ref([])
    const programsLocale: Ref = ref()
    async function loadPrograms(): Promise<void> {
        try {
            programsLocale.value = appInstance.$i18n.locale.value
            programs.value = await appInstance.$api.packagePrograms.get()
        } catch (e) {
            programs.value = []
            programsLocale.value = null
        }
    }

    const loyalties: Ref = ref([])
    async function loadLoyalties(): Promise<void> {
        try {
            loyalties.value = (await appInstance.$api.loyalties.get()).loyalties
        } catch (e) {
            loyalties.value = []
        }
    }

    const contractGroups: Ref = ref([])
    async function loadContractGroups(): Promise<void> {
        try {
            const {contractGroups: groups} = await appInstance.$api.contractGroups.get()
            contractGroups.value = groups.sort((a, b) => a.name.localeCompare(b.name))
        } catch (e) {
            contractGroups.value = []
        }
    }

    const vatPlans: Ref = ref([])
    async function loadVatPlans(): Promise<void> {
        try {
            vatPlans.value = (await appInstance.$api.vatPlans.get()).vatPlans
        } catch (e) {
            vatPlans.value = []
        }
    }

    async function resetSalesChannels() {
        b2sList.value = []
        agenciesList.value = []
        corpList.value = []
        TOList.value = []
        b2sListLoaded.value = false
        agenciesLoaded.value = false
        corpLoaded.value = false
        TOLoaded.value = false
    }

    const b2sList: Ref<any[]> = ref([])
    const b2sListLoaded: Ref<boolean> = ref(false)
    const agenciesList: Ref<any[]> = ref([])
    const agenciesLoaded: Ref<boolean> = ref(false)
    const corpList: Ref<any[]> = ref([])
    const corpLoaded: Ref<boolean> = ref(false)
    const TOList: Ref<any[]> = ref([])
    const TOLoaded: Ref<boolean> = ref(false)
    const isLoadingSalesChannels: Ref<boolean> = ref(false)

    const loadCompaniesByType = async type => {
        let list, loadedFlag
        switch (type) {
            case 'TOUR_AGENCY':
                list = agenciesList
                loadedFlag = agenciesLoaded
                break
            case 'CORPORATOR':
                list = corpList
                loadedFlag = corpLoaded
                break
            case 'TOUR_OPERATOR':
                list = TOList
                loadedFlag = TOLoaded
                break
        }

        try {
            isLoadingSalesChannels.value = true
            list.value = await loadCompanies(type)
            loadedFlag.value = true
        } catch (e) {
            list.value = []
        } finally {
            isLoadingSalesChannels.value = false
        }
    }

    const loadB2C = async () => {
        try {
            isLoadingSalesChannels.value = true
            b2sList.value = (await appInstance.$api.b2c.get()).b2cs
                .sort((a, b) => a.name.localeCompare(b.name))
                .map(({name, b2cId, active}) => ({
                    id: b2cId,
                    name,
                    active: active ?? true,
                }))
            b2sListLoaded.value = true
        } catch (e) {
            b2sList.value = []
        } finally {
            isLoadingSalesChannels.value = false
        }
    }

    const loadCompanies = async type => {
        try {
            return (
                await appInstance.$api.companies.get({
                    relationship: 'CLIENT',
                    type,
                    order: 'ALPHABET',
                    shortResponse: true,
                })
            ).companies.map(({id, name, active}) => ({id, name, active}))
        } catch (e) {
            return []
        }
    }

    const membershipLevels: Ref = ref([])
    async function loadMembershipLevels(): Promise<void> {
        try {
            membershipLevels.value = (await appInstance.$api.membershipLevels.get()).membershipLevels
        } catch (e) {
            membershipLevels.value = []
        }
    }

    const pspSettingsConditions: Ref = ref({})
    const loadPspSettingsConditions = async () => {
        try {
            pspSettingsConditions.value = (await appInstance.$api.pspSettingsConditions.get()).conditionValueMap
        } catch (e) {
            pspSettingsConditions.value = {}
        }
    }

    const valueTypeConditions: Ref = ref({})
    const loadValueTypeConditions = async () => {
        try {
            valueTypeConditions.value = (await appInstance.$api.valueTypeConditions.get()).object
        } catch (e) {
            valueTypeConditions.value = {}
        }
    }

    const conditionItemTypes: Ref = ref({})
    const loadConditionItemTypes = async () => {
        try {
            conditionItemTypes.value = (await appInstance.$api.conditionItemTypes.get()).object
        } catch (e) {
            conditionItemTypes.value = {}
        }
    }

    const salesConditions: Ref = ref([])
    const loadSalesConditions = async () => {
        try {
            salesConditions.value = (await appInstance.$api.salesItemTypes.get()).salesConditions
        } catch (e) {
            salesConditions.value = []
        }
    }

    const resetDataDependsOnContractId = () => {
        contract.value = {}
        fullContract.value = {}
        hotelInfo.value = {}

        catalogue.value = []
        paxes.value = []
        productServices.value = []
        mealTypes.value = []
        secondaryServices.value = []
        charterServiceCategories.value = []
    }

    return {
        orders,
        dailyRates,
        availability,
        services,
        tariffs,
        loadTariffs,
        tariffsForPrice,
        prices,
        contract,
        loadOrders,
        loadContract,
        loadAvailability,
        loadDailyRates,
        loadPrices,
        reload,
        clientInit,
        availabilityServices,
        loadFullContract,
        clearAllContractInfo,
        isLoading,
        fullContract,
        loadHotelInfo,
        isHotelInfoLoading,
        hotelInfo,
        loadPaxes,
        paxes,
        loadHotelChainDictionaryInfo,
        hotelChainDictionary,
        loadHotelServiceDictionaryInfo,
        hotelServiceDictionaryInfo,
        isAmenitiesLoading,
        loadCatalogue,
        catalogue,
        loadProductServices,
        productServices,
        loadCharterServiceCategories,
        charterServiceCategories,
        loadOffices,
        offices,
        loadHotelLocationDictionaryInfo,
        locationPointsDictionary,
        loadHotelCategoryDictionaryInfo,
        categoryDictionary,
        loadHotelTypesDictionaryInfo,
        hotelTypesDictionary,
        loadContentProviders,
        contentProviders,
        loadHotelData,
        loadHotelSecondaryServices,
        mealTypes,
        loadRoomTypeData,
        roomTypes,
        programs,
        programsLocale,
        loadPrograms,
        loadSuppliersForContract,
        internalSuppliers,
        loyalties,
        loadLoyalties,
        contractGroups,
        loadContractGroups,
        vatPlans,
        loadVatPlans,
        isLoadingSalesChannels,
        loadB2C,
        b2sListLoaded,
        b2sList,
        agenciesLoaded,
        agenciesList,
        corpLoaded,
        corpList,
        TOLoaded,
        TOList,
        loadCompaniesByType,
        membershipLevels,
        loadMembershipLevels,
        resetDataDependsOnContractId,
        secondaryServices,
        loadOwnProductSecondaryServices,
        loadPspSettingsConditions,
        pspSettingsConditions,
        valueTypeConditions,
        loadValueTypeConditions,
        conditionItemTypes,
        loadConditionItemTypes,
        salesConditions,
        loadSalesConditions,
    }
})

// TODO [P3] Need check this HRM improvement. Relates with https://pinia.vuejs.org/cookbook/hot-module-replacement.html#HMR-Hot-Module-Replacement-
if (import.meta.hot) import.meta.hot.accept(acceptHMRUpdate(useSupplierStore, import.meta.hot))
